import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Timer, LogOut, ArrowRight, Lock } from "lucide-react";
import { getStoredUser, getMsUntilExpiry, SESSION_CHANGE_EVENT } from "../../services/api";
import { logout as logoutRequest } from "../../services/authService";

const WARNING_AFTER_MS = 30 * 60 * 1000; // 30 minutes
const AUTO_LOGOUT_AFTER_MS = 30 * 1000; // 30 seconds after the warning shows

/**
 * Ports the Form 720 app's session-timeout popup exactly: a fixed 30-minute
 * timer shows a "Session Expiring Soon" warning, then auto-logs-out 30
 * seconds later if untouched. A separate absolute timer force-logs-out
 * exactly when the JWT itself expires (from expiresAt, set at login - see
 * authService.js's login()), independent of the 30-min/30-sec cycle.
 *
 * Like the Form 720 original, this is NOT real mouse/keyboard idle
 * detection - both timers start on mount or sign-in and "Continue" only resets
 * the JS timers (no backend "extend session" call exists, matching Form
 * 720, which has none either). Mounted once at the app root (see App.jsx)
 * so it runs for the whole SPA lifetime, not just inside protected routes.
 */
const SessionManager = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const jwtExpiryTimerRef = useRef(null);

  const startWarningTimer = () => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    warningTimerRef.current = setTimeout(() => {
      setShowPopup(true);
      logoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, AUTO_LOGOUT_AFTER_MS);
    }, WARNING_AFTER_MS);
  };

  const startJwtExpiryTimer = () => {
    clearTimeout(jwtExpiryTimerRef.current);
    const ms = getMsUntilExpiry();
    if (ms == null) return; // no expiresAt on the stored user - nothing to schedule
    jwtExpiryTimerRef.current = setTimeout(() => handleLogout(true), ms);
  };

  const handleContinue = () => {
    setShowPopup(false);
    clearTimeout(logoutTimerRef.current);
    startWarningTimer();
  };

  const handleLogout = async (jwtExpired = false) => {
    setShowPopup(false);
    await logoutRequest();
    navigate(jwtExpired ? "/login?expired=1" : "/login");
  };

  useEffect(() => {
    const clearTimers = () => {
      clearTimeout(warningTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearTimeout(jwtExpiryTimerRef.current);
    };
    // Arms both timers when a user is stored, stops them when not. Runs on
    // mount (covers a hard refresh while signed in) and on every sign-in /
    // sign-out (SESSION_CHANGE_EVENT from api.js) - sign-in navigates inside
    // the SPA, so the mount-time check alone never armed the timers for it.
    const syncWithSession = () => {
      clearTimers();
      setShowPopup(false);
      if (getStoredUser()) {
        startWarningTimer();
        startJwtExpiryTimer();
      }
    };
    syncWithSession();
    window.addEventListener(SESSION_CHANGE_EVENT, syncWithSession);
    return () => {
      window.removeEventListener(SESSION_CHANGE_EVENT, syncWithSession);
      clearTimers();
    };
    // Re-arms only on mount and on session changes, not on route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-[400px] max-w-[90vw] rounded-[20px] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden">
        <div className="h-1.5 bg-[var(--accent)]" />
        <div className="p-7 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--amber-soft)]">
            <Timer size={28} className="text-[var(--amber)]" />
          </div>
          <h2 className="text-lg font-semibold text-[var(--ink)]">Session Expiring Soon</h2>
          <p className="mt-2 text-sm text-[var(--muted)]">
            You've been inactive for a while. Would you like to continue your session or sign out?
          </p>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => handleLogout()}
              className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-[10px] border border-[var(--line)] bg-[var(--panel)] text-sm font-semibold text-[var(--ink)] hover:bg-[var(--panel-2)]"
            >
              <LogOut size={16} />
              Sign Out
            </button>
            <button
              type="button"
              onClick={handleContinue}
              className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-[10px] bg-[var(--accent-fill)] text-sm font-semibold text-[var(--accent-ink)] hover:bg-[var(--accent-fill-hover)]"
            >
              Continue
              <ArrowRight size={16} />
            </button>
          </div>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[var(--dim)]">
            <Lock size={12} />
            Your data is saved and secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;
